/**
 * slotLabelUtils
 * ---------------
 * Utility helpers for deriving and assigning rage room "Smasher" labels
 * based on the current grid availability and booking payloads.
 */
const DEFAULT_LABEL_PREFIX = 'Smasher';

const normalizeLabel = (label) => (typeof label === 'string' ? label.trim().toLowerCase() : '');

const getBookingIdentifier = (booking) =>
  booking?.bookingId ?? booking?._id ?? booking?.id ?? null;

export const parseSlotLabelsInput = (input) => {
  if (Array.isArray(input)) {
    return input
      .map((label) => (typeof label === 'string' ? label.trim() : ''))
      .filter(Boolean);
  }
  if (typeof input === 'string') {
    return input
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean);
  }
  return [];
};

const extractSlotLabelsFromBooking = (booking) => {
  if (!booking) {
    return [];
  }
  const labels = [
    ...parseSlotLabelsInput(booking.slotLabels),
    ...parseSlotLabelsInput(booking.slotLabel),
  ];
  return labels.filter(Boolean);
};

export const deriveSlotLabelPool = ({ cell, slotLabelSuggestions = [] } = {}) => {
  if (Array.isArray(cell?.slots) && cell.slots.length) {
    return cell.slots.map(
      (slot, index) => slot.slotLabel || `${DEFAULT_LABEL_PREFIX} ${index + 1}`
    );
  }

  if (slotLabelSuggestions.length) {
    return slotLabelSuggestions;
  }

  const fallbackCount = cell?.totalCapacity || 0;
  if (fallbackCount > 0) {
    return Array.from({ length: fallbackCount }, (_, index) => `${DEFAULT_LABEL_PREFIX} ${index + 1}`);
  }

  return [];
};

export const collectReservedSlotLabels = ({ cell, bookingToExclude } = {}) => {
  if (!cell?.bookings?.length) {
    return [];
  }

  const excludeId = getBookingIdentifier(bookingToExclude);

  return cell.bookings
    .filter((booking) => {
      if (!excludeId) {
        return true;
      }
      return getBookingIdentifier(booking) !== excludeId;
    })
    .flatMap((booking) => extractSlotLabelsFromBooking(booking))
    .filter(Boolean);
};

export const computeSlotLabelAvailability = ({
  cell,
  bookingToExclude,
  slotLabelSuggestions = [],
} = {}) => {
  const slotLabelPool = deriveSlotLabelPool({ cell, slotLabelSuggestions });
  const reservedSlotLabels = collectReservedSlotLabels({ cell, bookingToExclude });

  if (!slotLabelPool.length) {
    return {
      slotLabelPool,
      reservedSlotLabels,
      availableSlotLabels: [],
    };
  }

  const reservedSet = new Set(reservedSlotLabels.map(normalizeLabel));
  const availableSlotLabels = slotLabelPool.filter(
    (label) => label && !reservedSet.has(normalizeLabel(label))
  );

  return { slotLabelPool, reservedSlotLabels, availableSlotLabels };
};

export const generateAutoSlotLabels = ({
  partySize,
  availableSlotLabels = [],
  slotLabelPool = [],
  reservedSlotLabels = [],
  preferredSlotLabels = [],
} = {}) => {
  const requestedSize = Number(partySize);
  if (!requestedSize || Number.isNaN(requestedSize) || requestedSize <= 0) {
    return '';
  }

  const selection = [];
  const taken = new Set();
  const reservedSet = new Set(reservedSlotLabels.map(normalizeLabel));
  const availableSet = new Set(availableSlotLabels.map(normalizeLabel));
  const preferredList = parseSlotLabelsInput(preferredSlotLabels);

  const pushLabel = (label) => {
    if (!label || selection.length >= requestedSize) {
      return;
    }
    const normalized = normalizeLabel(label);
    if (!normalized || taken.has(normalized)) {
      return;
    }

    const isReserved = reservedSet.has(normalized);
    const isPreferred = preferredList.some(
      (preferred) => normalizeLabel(preferred) === normalized
    );
    const hasAvailabilityContext = availableSet.size > 0;

    if (isReserved && !isPreferred) {
      return;
    }

    if (hasAvailabilityContext && !isPreferred && !availableSet.has(normalized)) {
      return;
    }

    selection.push(label);
    taken.add(normalized);
  };

  preferredList.forEach(pushLabel);
  availableSlotLabels.forEach(pushLabel);

  if (!availableSlotLabels.length && slotLabelPool.length) {
    slotLabelPool.forEach(pushLabel);
  }

  let fallbackIndex = slotLabelPool.length ? slotLabelPool.length + 1 : 1;
  while (selection.length < requestedSize) {
    const fallbackLabel = `${DEFAULT_LABEL_PREFIX} ${fallbackIndex}`;
    fallbackIndex += 1;
    const normalizedFallback = normalizeLabel(fallbackLabel);
    if (reservedSet.has(normalizedFallback) || taken.has(normalizedFallback)) {
      continue;
    }
    selection.push(fallbackLabel);
    taken.add(normalizedFallback);
  }

  return selection.slice(0, requestedSize).join(', ');
};